import { Controller } from "@hotwired/stimulus"

// Lazy-load fallback for analysis turbo-frames; syncs aria-busy when content leaves the throbber state.
export default class extends Controller {
  static values = {
    delay: { type: Number, default: 5000 },
    maxReloads: { type: Number, default: 8 },
    readyMessage: { type: String, default: "" },
    slowMessage: { type: String, default: "" }
  }

  connect() {
    this.reloadCount = 0
    this.onFrameLoad = this.handleFrameLoad.bind(this)
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.observer = new MutationObserver(() => this.syncBusyState())
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.syncBusyState()
    this.scheduleReload()
  }

  disconnect() {
    clearTimeout(this.timeoutId)
    this.observer?.disconnect()
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad)
  }

  syncBusyState() {
    const loading = this.element.querySelector(".throbber-loader") !== null
    const wasBusy = this.element.getAttribute("aria-busy") === "true"

    if (loading) {
      this.element.setAttribute("aria-busy", "true")
    } else {
      this.element.setAttribute("aria-busy", "false")
      clearTimeout(this.timeoutId)
      if (wasBusy) this.announceReady()
    }
  }

  announceReady() {
    const liveRegion = document.querySelector("[data-analysis-live-region]")
    if (!liveRegion) return

    const message = this.readyMessageValue
    if (!message) return

    liveRegion.textContent = message
  }

  scheduleReload() {
    clearTimeout(this.timeoutId)
    if (this.element.getAttribute("aria-busy") !== "true") return
    if (this.reloadCount >= this.maxReloadsValue) {
      this.showSlowHint()
      return
    }

    const delay = Math.round(this.delayValue * Math.pow(1.5, this.reloadCount))
    this.timeoutId = setTimeout(() => {
      this.reloadCount += 1
      this.element.reload()
    }, delay)
  }

  showSlowHint() {
    const hint = this.element.querySelector(".analysis-frame-polling-hint")
    if (!hint || !this.slowMessageValue) return

    hint.textContent = this.slowMessageValue
  }

  handleFrameLoad() {
    this.syncBusyState()
    this.scheduleReload()
  }
}
